import React, { useState, useRef } from 'react';
import { List } from 'types/api';
import { LabelDTO } from 'types/company';
import { IResource } from 'util/resource';
import Resources from 'util/resource/Resources';
import useNotify from 'hooks/useNotify';
import Dialog, { Content, DialogActions, Title } from 'ui/views/dialogs/Dialog';
import Button from 'ui/elements/buttons/Button';
import Checkbox from 'ui/elements/form/choice/Checkbox';
import LabelChip from 'ui/domain/Chips/LabelChip';
import IconButton from 'ui/elements/icons/IconButton';
import EditIcon from 'ui/elements/icons/EditIcon';
import TrashIcon from 'ui/elements/icons/TrashIcon';
import useLazyResource from 'util/resource/useLazyResource';
import LabelForm from './LabelsDialog/LabelsForm';
import PopMenu, { PopMenuItem } from 'ui/modules/PopMenu';
import ColorDot from 'ui/modules/ColorDot';
import QuickEditField from 'ui/elements/form/QuickEditField';
import bluePlanetTheme from 'ui/theme/themeBluePlanet';

const LABEL_COLORS = [
  { text: 'Grey', color: bluePlanetTheme.bluePlanetPalette.grey.medium as string },
  { text: 'Green Light', color: bluePlanetTheme.bluePlanetPalette.green.light as string },
  { text: 'Violet Light', color: bluePlanetTheme.bluePlanetPalette.violet.light as string },
  { text: 'Blue Light', color: bluePlanetTheme.bluePlanetPalette.blue.light as string },
  { text: 'Red Light', color: bluePlanetTheme.bluePlanetPalette.red.light as string },
  { text: 'Yellow Light', color: bluePlanetTheme.bluePlanetPalette.yellow.light as string },
  { text: 'Orange Light', color: bluePlanetTheme.bluePlanetPalette.orange.light as string },
  { text: 'Grey Main', color: bluePlanetTheme.bluePlanetPalette.grey.main as string },
  { text: 'Green', color: bluePlanetTheme.bluePlanetPalette.green.medium as string },
  { text: 'Violet', color: bluePlanetTheme.bluePlanetPalette.violet.medium as string },
  { text: 'Blue', color: bluePlanetTheme.bluePlanetPalette.blue.medium as string },
  { text: 'Red', color: bluePlanetTheme.bluePlanetPalette.red.medium as string },
  { text: 'Yellow', color: bluePlanetTheme.bluePlanetPalette.yellow.medium as string },
  { text: 'Orange', color: bluePlanetTheme.bluePlanetPalette.orange.medium as string },
  { text: 'Grey Dark', color: bluePlanetTheme.bluePlanetPalette.grey.dark as string },
];

interface LabelDeleteProps {
  label: LabelDTO;
  entityName: string;
  api: {
    delete: (label: LabelDTO) => Promise<void>;
  };
  onDeleteSuccess: () => void;
  onCancel: () => void;
  notify: (type: string, message: string) => void;
}

function LabelDelete({ label, entityName, api, onDeleteSuccess, onCancel, notify }: LabelDeleteProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await api.delete(label);
      onDeleteSuccess();
    } catch (e) {
      console.error('Failed to delete label:', e);
      notify('error', 'Failed to remove label');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Title onClose={onCancel}>Are you sure you want to delete this label?</Title>
      <Content>
        <p>This label will be removed from all {entityName}. There is no undo.</p>
      </Content>
      <DialogActions>
        <Button isLoading={isDeleting} kind="primary" color="red" onClick={handleDelete}>
          Delete
        </Button>
        <Button onClick={onCancel} kind="tertiary">
          Cancel
        </Button>
      </DialogActions>
    </>
  );
}

interface LabelsDialogProps {
  labelsResource: IResource<List<LabelDTO>>;
  initialLabels?: LabelDTO[];
  mutateLabels: () => void;
  title: string;
  onClose: () => void;
  api: {
    create: (name: string, color: string) => Promise<LabelDTO>;
    patch: (label: LabelDTO, name: string, color: string) => Promise<LabelDTO>;
    delete: (label: LabelDTO) => Promise<void>;
  };
  entityName: string;
  message?: React.ReactNode;
  onSave?: (selectedLabels: string[], deselectedLabels: string[]) => void;
  entities: (number | string)[];
  update?: (entities: (number | string)[], selectedLabels: string[], deselectedLabels: string[]) => Promise<any>;
}

// We use a mode state so we can switch between the bulk editing view and the form view.
type Mode = { type: 'bulk' } | { type: 'form'; label?: LabelDTO };

export default function LabelsDialog({
  labelsResource,
  initialLabels,
  mutateLabels,
  title,
  onClose,
  api,
  entityName,
  onSave,
  entities,
  update,
  message,
}: LabelsDialogProps) {
  const notify = useNotify();
  const isInAssignMode = !!update && entities.length !== 0;

  const [mode, setMode] = useState<Mode>({ type: 'bulk' });
  const [selectedLabels, setSelectedLabels] = useState<string[]>(initialLabels?.map(l => l.id) ?? []);
  const [deselectedLabels, setDeselectedLabels] = useState<string[]>([]);
  const [editingLabelId, setEditingLabelId] = useState<string | undefined>(undefined);
  const [editingLabelName, setEditingLabelName] = useState<string>('');
  const [isCreatingNewLabel, setIsCreatingNewLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [deleteLabel, setDeleteLabel] = useState<LabelDTO | null>(null);
  const editInputRef = useRef<HTMLInputElement | undefined>();
  const newLabelInputRef = useRef<HTMLInputElement | undefined>();

  const handleCancel = () => setMode({ type: 'bulk' });
  const handleFormSubmitSuccess = () => {
    mutateLabels();
    setMode({ type: 'bulk' });
  };
  const handleDeleteRequest = (label: LabelDTO) => setDeleteLabel(label);
  const handleDeleteSuccess = () => {
    mutateLabels();
    setDeleteLabel(null);
  };
  const handleDeleteCancel = () => setDeleteLabel(null);

  // Direct color change handler
  const handleColorChange = async (label: LabelDTO, color: string) => {
    try {
      await api.patch(label, label.name, color);
      mutateLabels();
    } catch (e) {
      notify('error', 'Unable to update label color');
    }
  };

  // Start editing label name
  const startEditingLabel = (label: LabelDTO) => {
    setEditingLabelId(label.id);
    setEditingLabelName(label.name);
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  // Save label name change
  const saveLabelName = async (label: LabelDTO) => {
    if (editingLabelName.trim() && editingLabelName !== label.name) {
      try {
        await api.patch(label, editingLabelName.trim(), label.color);
        mutateLabels();
      } catch (e) {
        notify('error', 'Unable to update label name');
      }
    }
    setEditingLabelId(undefined);
    setEditingLabelName('');
  };

  // Cancel editing label name
  const cancelEditingLabel = () => {
    setEditingLabelId(undefined);
    setEditingLabelName('');
  };

  // Start creating new label
  const startCreatingLabel = () => {
    setIsCreatingNewLabel(true);
    setNewLabelName('');
    setTimeout(() => newLabelInputRef.current?.focus(), 50);
  };

  // Save new label
  const saveNewLabel = async (allLabels: LabelDTO[]) => {
    if (newLabelName.trim()) {
      // Find first unused color
      const usedColors = allLabels.map(l => l.color);
      const availableColor = LABEL_COLORS.find(c => !usedColors.includes(c.color))?.color || LABEL_COLORS[0].color;

      try {
        await api.create(newLabelName.trim(), availableColor);
        mutateLabels();
        setIsCreatingNewLabel(false);
        setNewLabelName('');
      } catch (e) {
        notify('error', 'Unable to create label');
      }
    }
  };

  // Cancel creating new label
  const cancelCreatingLabel = () => {
    setIsCreatingNewLabel(false);
    setNewLabelName('');
  };

  // Toggle the state of a label. For bulk editing we cycle between:
  // - default (unchanged) → selected (to add) → deselected (to remove) → back to default.
  const toggleLabel = (labelId: UUID) => {
    const isSelected = selectedLabels.includes(labelId);
    const isDeselected = deselectedLabels.includes(labelId);

    // When bulk editing we have three states, selected, deselected and indeterminate
    // For only one company we can just toggle between selected and deselected
    if (isSelected) {
      setSelectedLabels(selectedLabels.filter(id => id !== labelId));
      setDeselectedLabels([...deselectedLabels, labelId]);
    } else if (isDeselected) {
      setDeselectedLabels(deselectedLabels.filter(id => id !== labelId));

      // If we only have one entity (initialLabels are set), we don't need the indeterminate state
      if (initialLabels) {
        setSelectedLabels([...selectedLabels, labelId]);
      }
    } else {
      setSelectedLabels([...selectedLabels, labelId]);
    }
  };

  const [handleUpdate, isSaving] = useLazyResource(
    ({
      entities,
      selectedLabels,
      deselectedLabels,
    }: {
      entities: (number | UUID)[];
      selectedLabels: UUID[];
      deselectedLabels: UUID[];
    }) =>
      update?.(
        entities,
        // In single edit mode, we don't update the labels that were already assigned
        // It doesn't make a difference for a single user/tab, but could avoid unintentional overwrites for simultaneous edits
        initialLabels ? selectedLabels.filter(id => !initialLabels.map(l => l.id).includes(id)) : selectedLabels,
        deselectedLabels,
      ) ?? Promise.resolve(),
    {
      onSuccess: (_, { selectedLabels, deselectedLabels }) => {
        onSave?.(selectedLabels, deselectedLabels);
        onClose();
      },
      onFailure: () => notify('error', `Unable to update label assignments`),
    },
  );

  // Allow the dialog to be closed directly only in bulk mode.
  const dialogClose = mode.type === 'bulk' ? onClose : () => setMode({ type: 'bulk' });

  return (
    <>
      <Dialog open onClose={dialogClose} mobileLayout="drawer">
        <Title onClose={dialogClose}>{title}</Title>
        <Resources resources={[labelsResource]}>
          {([labels]: [List<LabelDTO>]) => {
            switch (mode.type) {
              case 'bulk':
                return (
                  <>
                    <Content>
                      {isInAssignMode && (
                        <div className="u-content-spacing-bottom">
                          <p className="u-quarter-spacing-bottom">
                            Select labels to assign or remove from{' '}
                            <>
                              {entities.length === 1 ? (
                                entityName
                              ) : (
                                <>
                                  <span className="text-weight-medium">{entities.length}</span> {entityName}
                                  {entityName.endsWith('s') ? '' : 's'}
                                </>
                              )}
                            </>
                            :
                          </p>
                          <p className="text-grey text-small">
                            {initialLabels
                              ? 'Check to assign, uncheck to remove labels.'
                              : 'Click once to add, twice to remove.'}
                          </p>
                        </div>
                      )}
                      <div className="u-flex u-flex--column">
                        {labels.values.map(label => {
                          const isEditing = editingLabelId === label.id;
                          const colorItems: PopMenuItem[] = LABEL_COLORS.map(({ text, color }) => ({
                            text: (
                              <>
                                <ColorDot size="large" className="u-quarter-spacing-right" color={color} />
                                <span className="text-medium">{text}</span>
                              </>
                            ),
                            onClick: () => handleColorChange(label, color),
                            spacing: 'compact' as const,
                          }));

                          return (
                            <div
                              key={label.id}
                              className="u-flex u-flex-space-between u-flex-align-center u-half-spacing-bottom"
                            >
                              {isInAssignMode ? (
                                <Checkbox
                                  label={
                                    isEditing ? (
                                      <QuickEditField<string>
                                        inputRef={editInputRef}
                                        name={`label-${label.id}`}
                                        value={editingLabelName}
                                        onChange={e => setEditingLabelName(e.target.value)}
                                        onCancel={cancelEditingLabel}
                                        onConfirm={() => saveLabelName(label)}
                                        height={24}
                                        fontSize="small"
                                      />
                                    ) : (
                                      <LabelChip label={label.name} color={label.color} />
                                    )
                                  }
                                  checked={selectedLabels.includes(label.id)}
                                  indeterminate={!initialLabels && deselectedLabels.includes(label.id)}
                                  color={deselectedLabels.includes(label.id) ? 'secondary' : 'primary'}
                                  onChange={() => toggleLabel(label.id)}
                                />
                              ) : isEditing ? (
                                <QuickEditField<string>
                                  inputRef={editInputRef}
                                  name={`label-${label.id}`}
                                  value={editingLabelName}
                                  onChange={e => setEditingLabelName(e.target.value)}
                                  onCancel={cancelEditingLabel}
                                  onConfirm={() => saveLabelName(label)}
                                  height={24}
                                  fontSize="small"
                                />
                              ) : (
                                <LabelChip label={label.name} color={label.color} />
                              )}
                              <div className="u-flex u-flex-align-center u-flex--gap-half">
                                <PopMenu
                                  items={colorItems}
                                  style={{ minWidth: '250px' }}
                                  renderAnchor={(onClick, _isOpen) => (
                                    <IconButton size="small" onClick={onClick} sx={{ padding: 0 }}>
                                      <ColorDot size="large" color={label.color} />
                                    </IconButton>
                                  )}
                                />
                                <IconButton color="indigo" onClick={() => startEditingLabel(label)} sx={{ padding: 0 }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  color="indigo"
                                  onClick={() => handleDeleteRequest(label)}
                                  sx={{ padding: 0 }}
                                >
                                  <TrashIcon fontSize="small" />
                                </IconButton>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {message}
                      {isCreatingNewLabel ? (
                        <div className="u-content-spacing-top">
                          <QuickEditField<string>
                            inputRef={newLabelInputRef}
                            name="new-label"
                            value={newLabelName}
                            placeholder="Enter label name"
                            onChange={e => setNewLabelName(e.target.value)}
                            onCancel={cancelCreatingLabel}
                            onConfirm={() => saveNewLabel(labels.values)}
                            height={40}
                            fontSize="small"
                          />
                        </div>
                      ) : (
                        <Button kind="tertiary" onClick={startCreatingLabel} className="u-content-spacing-top">
                          + Add another label
                        </Button>
                      )}
                    </Content>
                    <DialogActions>
                      {entities.length > 0 && (
                        <>
                          {update ? (
                            <>
                              <Button
                                isLoading={isSaving}
                                onClick={() => handleUpdate({ entities, selectedLabels, deselectedLabels })}
                                kind="primary"
                                disabled={entities.length === 0}
                              >
                                Update
                              </Button>
                              <Button onClick={onClose} kind="tertiary">
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button onClick={onClose} kind="secondary">
                              Close
                            </Button>
                          )}
                        </>
                      )}
                    </DialogActions>
                  </>
                );
              case 'form':
                return (
                  <LabelForm
                    label={mode.label}
                    api={api}
                    onSubmitSuccess={handleFormSubmitSuccess}
                    onCancel={handleCancel}
                    onDeleteRequest={mode.label ? () => handleDeleteRequest(mode.label!) : undefined}
                    notify={notify}
                  />
                );
              default:
                return null;
            }
          }}
        </Resources>
      </Dialog>
      {deleteLabel && (
        <Dialog open onClose={handleDeleteCancel} mobileLayout="drawer">
          <LabelDelete
            label={deleteLabel}
            entityName={entityName}
            api={api}
            onDeleteSuccess={handleDeleteSuccess}
            onCancel={handleDeleteCancel}
            notify={notify}
          />
        </Dialog>
      )}
    </>
  );
}
